import { BLOB_METADATA_API, isDefined } from '@aloft/toolkit-shared';
import { Flex, Icon, IconButton, ProductColor, Typography } from '@aloft/toolkit-sui';

import { useGetBlobMetadata } from '../hooks/api/toolkitComponents';

type BaseProps = {
  onDownloadClick?: () => void;
  onDeleteClick?: () => void;
  isError?: boolean;
  isDisabled?: boolean;
  isSuccess?: boolean;
};

type PropsWithBlobID = BaseProps & {
  blobID: string;
  filename?: string;
};
type PropsWithoutBlobID = BaseProps & {
  blobID?: undefined;
  filename: string;
};

type FileUploaderFileItemProps = PropsWithBlobID | PropsWithoutBlobID;

export const FileUploaderFileItem = ({
  blobID,
  filename,
  onDownloadClick,
  onDeleteClick,
  isError = false,
  isDisabled = false,
  isSuccess = false,
}: FileUploaderFileItemProps) => {
  const metadata = useGetBlobMetadata(
    { pathParams: { blobID: isDefined(blobID) ? blobID : 'notEnabled' } },
    { enabled: isDefined(blobID), refetchOnWindowFocus: false, staleTime: Infinity },
  );

  const data =
    metadata.data === undefined ? undefined : BLOB_METADATA_API.GET.RESPONSE.parse(metadata.data);

  return (
    <Flex alignItems="center" gap={1}>
      {isSuccess && <Icon icon="check" color="success" size="md" />}
      <Typography color={getFileUploaderTextColor(isDisabled, isError)} textAlign="center">
        {(blobID !== undefined && data !== undefined ? data.name : filename) ?? ''}
      </Typography>
      {onDownloadClick && !isDisabled && (
        <IconButton
          ariaLabel="download file"
          variant="tertiary"
          icon="download"
          onClick={onDownloadClick}
          size="sm"
        />
      )}
      {onDeleteClick && !isDisabled && (
        <IconButton
          ariaLabel="delete file"
          variant="tertiary"
          icon="delete-bin"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick();
          }}
          size="sm"
        />
      )}
    </Flex>
  );
};

const getFileUploaderTextColor = (isDisabled?: boolean, isError?: boolean): ProductColor => {
  if (isDisabled) {
    return 'textDisabled';
  }
  if (isError) {
    return 'error';
  }
  return 'textPrimary';
};
